import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FilterDescriptor, orderBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import {
  SubscriptionRequestCancellationStatus,
  SubscriptionRequestCancellationStatusValueType,
} from 'api/Trading-Accounts/Types/SubscriptionRequestCancellationStatus'
import {
  UnsubscribeRequestDto,
  UnsubscribeRequestFilterKey,
} from 'api/Trading-Accounts/Types/UnsubscribeRequestDto'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getUnsubscribeRequestFilterFields } from 'components/Filter/unsubscribeRequestsFields'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { ChevronDown } from 'lucide-react'
import { SelectClientEvent, SelectClientTable } from 'routes/Records/Clients/SelectClientTable'
import { useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  selectGridFilterByFilterId,
} from 'store/grid'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { unsubscribeRequestColumns } from './unsubscribeRequestColumns'

const useMapStateToProps = () => ({
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const TransactionUnsubscribeRequestsOverview = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()
  const [unsubscribeRequests, setUnsubscribeRequests] = useState<UnsubscribeRequestDto[]>([])

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { gridFilterByFilterIdSelector } = useMapStateToProps()

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response =
        await tradingAccountsApiClient.getTradingAccountSubscriptionUnsubscribeRequests({
          search: searchQuery,
          pageIndex,
          pageSize,
          skipCount: tableData?.skipCount !== false,
        })
      const total = response.totalResults

      if (response?.items) {
        setUnsubscribeRequests(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [getQuery, gridActions, gridState.pageSize, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()

    const response =
      await tradingAccountsApiClient.getTradingAccountSubscriptionUnsubscribeRequests({
        search: searchQuery,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skipCount,
      })
    const total = response.totalResults

    if (response?.items) {
      setUnsubscribeRequests(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })
    const response =
      await tradingAccountsApiClient.getTradingAccountSubscriptionUnsubscribeRequests({
        search: searchQuery,
        pageIndex,
        pageSize: gridState.take,
        skipCount: tableData?.skipCount !== false,
      })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setUnsubscribeRequests(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()

    const response =
      await tradingAccountsApiClient.getTradingAccountSubscriptionUnsubscribeRequests({
        search: searchQuery,
        pageIndex: 1,
        pageSize,
        skipCount: tableData?.skipCount,
      })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setUnsubscribeRequests(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<UnsubscribeRequestDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }
  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }
  const handleClientModalClose = () => {
    setClientModalOpen(false)
  }
  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }
  const handleGridSelectionChange = (event: GridSelectionChangeEvent) => {
    const index = unsubscribeRequests.findIndex((item) => item.id === event.dataItem.id)
    if (index !== -1) {
      const updatedItem = { ...unsubscribeRequests[index], selected: !event.dataItem.selected }
      setUnsubscribeRequests([
        ...unsubscribeRequests.slice(0, index),
        updatedItem,
        ...unsubscribeRequests.slice(index + 1),
      ])
    }
  }
  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setUnsubscribeRequests(
      unsubscribeRequests.map((item) => {
        return {
          ...item,
          selected: isChecked,
        }
      })
    )
  }
  const updateUnsubscribeRequest = async (
    dataItem: UnsubscribeRequestDto,
    status: SubscriptionRequestCancellationStatusValueType
  ) => {
    await tradingAccountsApiClient.updateTradingAccountSubscriptionUnsubscribeRequest(dataItem.id, {
      description: dataItem.description,
      status,
    })
    filterFetch()
  }

  const handleOnApprove = (dataItem: UnsubscribeRequestDto) =>
    updateUnsubscribeRequest(dataItem, SubscriptionRequestCancellationStatus.Approved)

  const handleOnReject = (dataItem: UnsubscribeRequestDto) =>
    updateUnsubscribeRequest(dataItem, SubscriptionRequestCancellationStatus.Rejected)

  const handleOnBulkApprove = async () => {
    await tradingAccountsApiClient.approveTradingAccountSubscriptionUnsubscribeRequests({
      ids: selectedItems,
    })
    filterFetch()
  }
  const handleOnBulkReject = async () => {
    await tradingAccountsApiClient.rejectTradingAccountSubscriptionUnsubscribeRequests({
      ids: selectedItems,
    })
    filterFetch()
  }
  const selectedItems = useMemo(() => {
    return unsubscribeRequests.filter((request) => request.selected).map((request) => request.id)
  }, [unsubscribeRequests])

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<UnsubscribeRequestDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const response =
            await tradingAccountsApiClient.getTradingAccountSubscriptionUnsubscribeRequests({
              search: searchQuery,
              pageIndex: gridFilterState.pageIndex,
              pageSize: gridFilterState.take,
              skipCount: tableData?.skipCount !== false,
            })

          if (response?.items) {
            setUnsubscribeRequests(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
    gridActions.setGridFilters({ ...gridState, sort: [] })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isPermitted = () =>
    hasPermission(permissions, PermissionsEnum.CanViewSubscriptionRequestCancellations)

  const isMenuItemDisabled =
    !hasPermission(permissions, PermissionsEnum.CanViewSubscriptionRequestCancellations) ||
    !selectedItems.length

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      <Breadcrumbs
        crumbs={[
          {
            title: 'Unsubscribe Requests with open Positions',
            link: links.transactionUnsubscribeRequests.url,
          },
        ]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterType={FilterType.UnsubscribeRequests}
        filterFetch={filterFetch}
        filterFields={[
          {
            name: UnsubscribeRequestFilterKey.client,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getUnsubscribeRequestFilterFields(),
        ]}
        getQuery={getQuery}
        extraHeaderActions={
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button type='button' variant='outline' aria-label='More options' />}
            >
              Actions <ChevronDown />
            </DropdownMenuTrigger>

            <DropdownMenuContent align='end'>
              <DropdownMenuItem disabled={isMenuItemDisabled} onSelect={handleOnBulkApprove}>
                Approve All
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleOnBulkReject} disabled={isMenuItemDisabled}>
                Reject All
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={unsubscribeRequests} columns={unsubscribeRequestColumns}>
          <Grid
            {...gridState}
            resizable
            data={orderBy(unsubscribeRequests, gridState.sort)}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            selectedField='selected'
            onSelectionChange={handleGridSelectionChange}
            onHeaderSelectionChange={handleHeaderSelectionChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
            sortable={{ allowUnsort: true, mode: 'multiple' }}
            onSortChange={(e) => {
              gridActions.setGridFilters({ ...gridState, sort: e.sort })
            }}
            sort={gridState.sort}
          >
            <Column
              field='selected'
              width='50px'
              headerSelectionValue={
                unsubscribeRequests.every((dataItem) => dataItem.selected) &&
                !!unsubscribeRequests.length
              }
            />
            {unsubscribeRequestColumns
              .filter((c) => c.show)
              .map((column) => {
                return <Column key={column.field} {...column} />
              })}
            <Column
              width={50}
              title=''
              cell={(gridCellProps: GridCellProps) => {
                if (
                  gridCellProps.dataItem.status.id ===
                  SubscriptionRequestCancellationStatus.Cancelled
                ) {
                  return <td />
                }
                return (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Approve',
                          disabled: !isPermitted(),
                          onClick: () => {
                            handleOnApprove(gridCellProps.dataItem)
                          },
                        },
                        {
                          name: 'Reject',
                          disabled: !isPermitted(),
                          onClick: () => {
                            handleOnReject(gridCellProps.dataItem)
                          },
                        },
                      ]}
                    />
                  </td>
                )
              }}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default TransactionUnsubscribeRequestsOverview
